import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate,  NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchData,
  selectImageUrl,
  selectIsLoading,
  selectSuggestionsLoading,
  selectSuggestionsError,
  selectUser,
  selectTilesDetails,
  selectRestrictedDataError,
  setChatbotId,
  resetState,
  setSuggestions,
  fetchImage as fetchImageAction,
  selectCopiedPost,
  selectOpenAiTokens,
  fetchUserData,
  selectPremiumGranted,
  selectGenerationCounts,
  selectActiveSubscription,
  fetchActiveSubscription,
} from "../../../../mainApp/features/DataSlice/dataSlice";
import {
  ChatbotContainer,
  ChatbotMain,
  ChatbotDetailsPanel,
  ChatbotForm,
  ChatbotInput,
  ChatbotExpandButton,
  ChatbotDetailsHeader,
  ChatbotError,
  AppTitle,
  LoadingElement,
  ImageDisplay,
  ImageWrapper,
  DownloadButtonContainer,
  SuccessCheckmark,
  ArtistImage
} from "./styled";
import {
  ChatbotButton,
  ChatbotMessageDescription,
  CopyButton,
  CopyButtonContainer
} from "../styled";

import styled from "styled-components";
import LoadingPage from "../../TechnicalTabs/LoadingPage";
import ErrorPage from "../../../../mainApp/features/TechnicalTabs/ErrorPage";
import { auth, db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { handleUnauthorizedAccess } from "../../Utility/handleUnauthorizedAccess";
import {
  IMAGE_FLUX_SCHNELL_COST,
  IMAGE_ESSENTIAL_V2_COST,
} from "../../Utility/tokenCosts";

// Select dla modelu/stylu/języka
const ChatbotSelect = styled.select`
  padding: 10px 15px;
  background-color: ${({ theme }) => theme.color.imageDarkGrey || "#1a1a1a"};
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.color.imageWhite || "#ffffff"};
  font-size: 14px;
  outline: none;
  transition: background-color 0.2s ease;
  height: 40px;
  box-sizing: border-box;
  cursor: pointer;

  &:focus {
    background-color: ${({ theme }) => theme.color.imageLightGrey || "#2a2a2a"};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.color.imageGrey || "#4a4a4a"};
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 12px;
    height: 34px;
  }
`;

const GenerationCounter = styled.span`
  font-size: 14px;
  color: ${({ remaining }) => (remaining > 0 ? '#1db954' : '#ff4d4d')};
  margin-right: 15px;
  font-weight: 600;
`;

const ImageChatbot = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const tilesDetails = useSelector(selectTilesDetails);
  const restrictedDataError = useSelector(selectRestrictedDataError);
  const suggestionsLoading = useSelector(selectSuggestionsLoading);
  const suggestionsError = useSelector(selectSuggestionsError);
  const imageUrl = useSelector(selectImageUrl);
  const copiedPost = useSelector(selectCopiedPost);
  const currentTokens = useSelector(selectOpenAiTokens);
  const premiumGranted = useSelector(selectPremiumGranted);
  const generationCounts = useSelector(selectGenerationCounts);
  const activeSubscription = useSelector(selectActiveSubscription);

  const [category, setCategory] = useState("");
  const [model, setModel] = useState("flux-schnell");

const STANDARD_MONTHLY_ID = process.env.REACT_APP_STANDARD_MONTHLY;
const STANDARD_YEARLY_ID = process.env.REACT_APP_STANDARD_YEARLY;

const isStandardPlan = activeSubscription && 
  (activeSubscription.priceId === STANDARD_MONTHLY_ID || activeSubscription.priceId === STANDARD_YEARLY_ID);

  const [style, setStyle] = useState("photorealism");
  const [language, setLanguage] = useState("polish"); // New state for language mode, default to Polish
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [copiedText, setCopiedText] = useState("");
  const [downloaded, setDownloaded] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [openAiTokens, setOpenAiTokensState] = useState(0);

  const validationToastId = 'image-creator-validation-toast';
  const limitToastId = 'image-creator-limit-toast'; // Nowe ID dla tosta z limitem

  const effectiveCopiedPost = copiedPost || location.state?.copiedPost;
  const delay = (ms) => new Promise((r) => setTimeout(r, ms));

  // Initialize
  useEffect(() => {
    dispatch(setChatbotId("image"));
    dispatch(resetState());
    dispatch(fetchData());
    return () => dispatch(resetState());
  }, [dispatch]);

  // Auth & user data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          dispatch(fetchData());
          dispatch(fetchUserData()); // Pobierz dane użytkownika
          dispatch(fetchActiveSubscription()); // Pobierz subskrypcję
          const ref = doc(db, "Users", currentUser.uid);
          const snap = await getDoc(ref);
          if (!snap.exists()) console.warn("Dokument użytkownika nie istnieje.");
          await delay(600);
        } catch (err) {
          console.error("Błąd pobierania danych użytkownika:", err);
          await delay(600);
        }
      } else {
        navigate("/login");
      }
      setInitialLoading(false);
    });
    return () => unsubscribe();
  }, [dispatch, navigate]);

  // Unauthorized redirect
  useEffect(() => {
    if (restrictedDataError) navigate("/unauthorized");
  }, [restrictedDataError, navigate]);

  useEffect(() => {
    if (currentTokens !== openAiTokens) {
      setOpenAiTokensState(currentTokens);
    }
  }, [currentTokens]);

  const imageGenerations = generationCounts?.image || 0;
  const remainingGenerations = 3 - imageGenerations;

  useEffect(() => {
    if (isStandardPlan) {
      const toastOptions = {
        toastId: limitToastId,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      };

      const message = `Limit generacji w planie Standard: ${remainingGenerations}/3`;

      if (toast.isActive(limitToastId)) {
        toast.update(limitToastId, { render: message });
      } else {
        toast.info(message, toastOptions);
      }
    }
  }, [isStandardPlan, remainingGenerations]);

  const handleChange = (e) => setCategory(e.target.value);
  
  const handleModelChange = (e) => {
    const value = e.target.value;
    setModel(value);
    if (value === "flux-schnell") {
      setStyle(null);
    } else {
      setStyle("photorealism");
    }
  };

  const handleStyleChange = (e) => setStyle(e.target.value);

  const handleLanguageChange = (e) => setLanguage(e.target.value); // New handler for language mode

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!category.trim()) {
      dispatch(setSuggestions("Proszę podać kategorię."));
      return;
    }

    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    const cost = model === "flux-schnell" ? IMAGE_FLUX_SCHNELL_COST : IMAGE_ESSENTIAL_V2_COST;
    if (currentTokens < cost) {
      toast.error("Masz za mało tokenów, aby wygenerować obraz.", toastOptions);
      return;
    }

    dispatch(fetchImageAction());
    dispatch({
      type: "FETCH_IMAGE",
      payload: {
        category,
        model,
        style: model === "essential-v2" ? style : undefined,
        language, // Include language in payload
      },
    });
  };

  const handleCopyAndShare = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(""), 2000);
      const encodedText = encodeURIComponent(text);
      const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?quote=${encodedText}`;
      window.open(facebookShareUrl, "_blank");
    } catch (err) {
      console.error("Błąd kopiowania:", err);
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        setCopiedText(text);
        setTimeout(() => setCopiedText(""), 2000);
        const encodedText = encodeURIComponent(text);
        const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?quote=${encodedText}`;
        window.open(facebookShareUrl, "_blank");
      } catch (execErr) {
        console.error("Błąd kopiowania (execCommand):", execErr);
      } finally {
        document.body.removeChild(textarea);
      }
    }
  };

  const handleDownloadImage = async (imageUrl) => {
    try {
      if (!imageUrl) {
        throw new Error("Brak URL obrazu");
      }
      const url = new URL(imageUrl);
      if (!url.searchParams.has("alt")) {
        url.searchParams.append("alt", "media");
      }
      const finalUrl = url.toString();
      const response = await fetch(finalUrl, {
        method: "GET",
        mode: "cors",
        headers: {
          Accept: "image/*",
        },
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Błąd HTTP: ${response.status} ${response.statusText} - ${errorText}`);
      }
      const blob = await response.blob();
      const urlObj = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = urlObj;
      link.download = `generated_image_${Date.now()}.jpeg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlObj);
      
      // Show success animation and toast
      setDownloaded(true);
      setShowSuccessAnimation(true);
      toast.success("Pobrano!", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      setTimeout(() => {
        setDownloaded(false);
        setShowSuccessAnimation(false);
      }, 2000);
    } catch (error) {
      console.error("Szczegółowy błąd podczas pobierania obrazu:", {
        message: error.message,
        name: error.name,
        stack: error.stack,
        imageUrl,
      });
      toast.error(`Nie udało się pobrać obrazu: ${error.message}. Sprawdź połączenie lub spróbuj ponownie.`, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const toggleDetailsPanel = () => setIsDetailsExpanded((p) => !p);

  if (initialLoading || isLoading === "loading") return <LoadingPage />;

  let returned;
  switch (isLoading) {
    case "success":
      if (!user) {
        returned = <LoadingPage isHighZIndex />;
      } else if (tilesDetails.length > 0 && user) {
        returned = (
          <ChatbotContainer isImage>
            <ChatbotMain isDetailsExpanded={isDetailsExpanded} isImage isDetailsPanelVisible>
              <AppTitle isImage>Generowanie Obrazów</AppTitle>
              <ChatbotForm onSubmit={handleSubmit} isImage>
                <ChatbotInput
                  type="text"
                  value={category}
                  onChange={handleChange}
                  placeholder='Wprowadź prostą instrukcję, np. "portret kobiety, farby olejne" lub...'
                  isImage
                />
                <ChatbotSelect value={model} onChange={handleModelChange}>
                  <option value="flux-schnell">Standard</option>
                  <option value="essential-v2">Premium</option>
                </ChatbotSelect>
                <ChatbotSelect
                  value={style || ""}
                  onChange={handleStyleChange}
                  disabled={model !== "essential-v2"}
                >
                  <option value="photorealism">Fotorealizm</option>
                  <option value="anime">Anime</option>
                  <option value="art">Art</option>
                </ChatbotSelect>
                <ChatbotSelect value={language} onChange={handleLanguageChange}>
                  <option value="polish">Polski</option>
                  <option value="english">Angielski</option>
                </ChatbotSelect>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {isStandardPlan && (
                    <GenerationCounter remaining={remainingGenerations}>
                      {remainingGenerations}/3
                    </GenerationCounter>
                  )}
                  <ChatbotButton
                    type="submit"
                    disabled={suggestionsLoading}
                    isImage
                    tokenCost={
                      model === "flux-schnell"
                        ? IMAGE_FLUX_SCHNELL_COST
                        : IMAGE_ESSENTIAL_V2_COST
                    }
                  >
                    Generuj obraz
                  </ChatbotButton>
                </div>
              </ChatbotForm>
              {suggestionsLoading ? (
                <LoadingElement isImage>Ładowanie</LoadingElement>
              ) : suggestionsError ? (
                <ChatbotError isImage>
                  {suggestionsError}
                </ChatbotError>
              ) : imageUrl ? (
                <>
                <ImageWrapper>
                  <ImageDisplay src={imageUrl} alt="Wygenerowany obraz" />
                  </ImageWrapper>
                  <DownloadButtonContainer>
  <CopyButton onClick={() => handleDownloadImage(imageUrl)} isImage isUnderImage>
    {downloaded ? "Pobrano!" : "Pobierz obraz"}
    {showSuccessAnimation && (
      <SuccessCheckmark>
        <svg className="loading-circle" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
        </svg>
        <div className="checkmark">✓</div>
      </SuccessCheckmark>
    )}
  </CopyButton>
</DownloadButtonContainer>
                </>
              ) : (
                <>
                <ChatbotMessageDescription isImage>
                  Stwórz opis, aby wygenerować to co chcesz. Przykłady:
                </ChatbotMessageDescription>
                <ChatbotMessageDescription isImage isDescription>
                  <span style={{ marginRight: 8, color: "#1e90ff" }}>•</span>
                 Stylowy kot w czerwonej czapce na skuterze, tło miejskie, ruch, energia, ulica pełna kolorów
                </ChatbotMessageDescription>
                <ChatbotMessageDescription isImage isDescription>
                  <span style={{ marginRight: 8, color: "#1e90ff" }}>•</span>
                  Kobieta z profesjonalnym makijażem, salon urody, zbliżenie na twarz, portret studyjny, centralny kadr, tylko głowa i szyja
                </ChatbotMessageDescription>
                 <ChatbotMessageDescription isImage isDescription>
                  <span style={{ marginRight: 8, color: "#1e90ff" }}>•</span>
                 Pompa ciepła zamontowana przy ścianie nowoczesnego domu, czysta elewacja, jasne dzienne światło, zadbany trawnik, centralne ujęcie urządzenia
                </ChatbotMessageDescription>
                    <ChatbotMessageDescription isImage isDescription>
                  <span style={{ marginRight: 8, color: "#1e90ff" }}>•</span>
                 Jeden rzeczoznawca ubezpieczeniowy w marynarce, trzyma notes, uśmiechnięty, patrzy w kamerę, jasne czyste tło
                </ChatbotMessageDescription>
                </>
              )}
            </ChatbotMain>
            <ChatbotDetailsPanel isDetailsExpanded={isDetailsExpanded} isImage>
              <ChatbotDetailsHeader isDetailsExpanded={isDetailsExpanded} isImage />
              <ChatbotExpandButton
                isDetailsExpanded={isDetailsExpanded}
                onClick={toggleDetailsPanel}
                isImage
              >
                {isDetailsExpanded ? "Zwiń szczegóły" : "Rozwiń szczegóły"}
              </ChatbotExpandButton>
              {effectiveCopiedPost ? (
                <>
                  <ChatbotMessageDescription isImage>
                    <strong style={{ display: "block", marginBottom: "1rem" }}>
                      Skopiowany post z Facebooka:
                    </strong>
                    {effectiveCopiedPost.split("\n").map((line, i) => {
                      const subheadingMatch = line.match(/^(\d+\.\s)(.+)/);
                      const inspirationMatch = line.match(/^-\s(.+)/);
                      const cleanedLine = line.replace(/\*\*(.*?)\*\*/g, "$1");
                      if (subheadingMatch) {
                        return (
                          <p key={i} style={{ fontWeight: "bold", margin: 0, whiteSpace: "pre-wrap" }}>
                            {cleanedLine}
                          </p>
                        );
                      } else if (inspirationMatch) {
                        return (
                          <p key={i} style={{ paddingLeft: 20, margin: 0, whiteSpace: "pre-wrap" }}>
                            {cleanedLine}
                          </p>
                        );
                      }
                      return (
                        <p key={i} style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                          {cleanedLine}
                        </p>
                      );
                    })}
                  </ChatbotMessageDescription>
                  <CopyButtonContainer>
                    <CopyButton onClick={() => handleCopyAndShare(effectiveCopiedPost)} isImage>
                      {copiedText === effectiveCopiedPost ? "Skopiowano!" : "Skopiuj tekst i udostępnij"}
                    </CopyButton>
                  </CopyButtonContainer>
                </>
              ) : (
                <>
                  <ChatbotMessageDescription isImage>
                    Stwórz post na Facebooka z pomocą naszego dedykowanego {" "}
                    <NavLink
                      to="/facebook"
                      style={{
                        textDecoration: "underline",
                        color: "inherit",
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.color = "inherit")}
                      onMouseOut={(e) => (e.currentTarget.style.color = "inherit")}
                      onClick={(e) => (e.currentTarget.style.color = "inherit")}
                    >
                      asystenta
                    </NavLink>, następnie kliknij "dodaj zdjęcie", aby zobaczyć go tutaj.
                  </ChatbotMessageDescription>
                <ArtistImage/>
                </>
              )}
            </ChatbotDetailsPanel>
          </ChatbotContainer>
        );
      } else {
        returned = handleUnauthorizedAccess();
      }
      break;
    case "error":
      returned = <ErrorPage />;
      break;
    default:
      returned = <LoadingPage />;
  }

  return returned;
};

export default ImageChatbot;